import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowDown as ICON_ARROW_DOWN } from 'react-feather';
import { uniqueId } from 'lodash';
import { Modal } from 'antd';

// redux
import { mapDispatchProps, mapStateToProps } from '../../../store/store';
import { connect } from 'react-redux';
import { debounce } from '../../../helpers/debounce';
import {
  getSessionStorage,
  removeSessionStorage,
  setSessionStorage,
} from '../../../helpers/sessionStorage';
import { ROUTES } from '../../../routes';
import API from '../../../api';

import PaymentCreate from '../../payments/create';
import PaymentComplete from '../../payments/complete';
import SNSBrandReview from '../brand_review';
import SNSShow from '../show';

import useInfiniteScroll from '../../../hooks/useInfiniteScrollBeta';
import Button from '../../../components/button';
import Checkbox from '../../../components/checkbox';
import Slider, { makeSettings } from '../../../components/slider';
import './styles.css';
import { useLanguage } from '@languages/index';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { PAYMENT_PRODUCT_TYPE } from 'constants/payment';

const SESSION_KEY_isPublishedFiltered = 'SNSIndex.isPublishedFiltered';
const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY);

const SNSIndex = (props: any) => {
  const language = useLanguage();
  const languageTexts = { ...language.common, ...language.pages.sns };
  const navigate = useNavigate();

  const storeSnsPass = () => {
    navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH, {
      replace: true,
    });
  };

  const [postings, setPostings] = useState<any>();
  const postingPage = useRef(1);
  const postingsCount = useRef(0);
  const isFetchingRef = useRef(false);

  const [isFetching, setIsFetching, isContentEnded, setIsContentEnded] =
    useInfiniteScroll(async () => {
      if (postings && postings.length) {
        await fetchPostings();
      }
    });

  const [isPublishedFiltered, setIsProvidedFiltered] = useState(
    getSessionStorage(SESSION_KEY_isPublishedFiltered) ?? null,
  );

  const [detailPostingId, setDetailPostingId] = useState<any>(null);

  const fetchPostings = async (refresh?) => {
    if (refresh) {
      postingPage.current = 1;
      setPostings(undefined);
    }

    if (isFetchingRef.current === true) return;
    isFetchingRef.current = true;
    setIsFetching(true);

    await API.Post.index({
      brandToken: props.auth.token,
      brandId: props.brand.id,
      page: postingPage.current,
      isPublished: isPublishedFiltered,
      isSns: true,
    })
      .then((response) => {
        postingsCount.current = response.data.count;

        setPostings(
          postingPage.current === 1
            ? response.data.postings
            : postings.concat(response.data.postings),
        );

        setIsContentEnded(
          response.data.paginator.currentPage >=
            response.data.paginator.lastPage,
        );

        setIsFetching(false);
        isFetchingRef.current = false;
        postingPage.current = postingPage.current + 1;
      })
      .catch((error) => {
        props.showModal({ text: error.message });
      });
  };

  const fetchPosting = async (postingId: number) => {
    await API.Post.show({
      brandToken: props.auth.token,
      postingId: postingId,
    }).then((response: any) => {
      setPostings(
        [...postings].map((item) => {
          if (item.id !== postingId) return item;

          let updated_posting = response.data.posting;
          for (let i in updated_posting.images) {
            updated_posting.images[i][
              'thumbSmUrl'
            ] += `?timestamp=${Date.now().toString()}`;
          }
          return updated_posting;
        }),
      );
    });
  };

  const togglePublishedAtFilter = (value?: string) => {
    if (isPublishedFiltered == value) {
      setIsProvidedFiltered(null);
      return false;
    }

    setIsProvidedFiltered(value);
  };

  useEffect(() => {
    fetchPostings(true);
  }, [isPublishedFiltered]);

  useEffect(() => {
    setSessionStorage(SESSION_KEY_isPublishedFiltered, isPublishedFiltered);
  }, [isPublishedFiltered]);

  const renderLoadingIndicator = () => {
    return isFetching && !isContentEnded ? (
      <div className="flex flex-row mx-10 my-10 justify-center items-center">
        <div className="flex flex-row justify-center text-center my-5">
          <span className="prose prose-xl text-slate-400">
            {languageTexts.loading_1}
          </span>
        </div>
      </div>
    ) : null;
  };

  const showPaymentCompleteModal = (
    targetProductType,
    amount,
    price,
    priceTotal,
  ) => {
    setTimeout(() => {
      props.showOverlays({
        content: () => {
          return (
            <PaymentComplete
              // title={languageTexts.payment_history2}
              itemType={targetProductType}
              // amount={amount}
              // price={price}
              // priceTotal={priceTotal}
              onConfirm={() => {
                props.closeOverlays();
              }}
            />
          );
        },
      });
    }, 400);
  };

  const proceedPayment = () => {
    let amount = undefined;
    let price = undefined;
    let priceTotal = undefined;

    props.closeOverlays();

    const targetProductType = props.brand?.hasExperiencedSnsInstall
      ? PAYMENT_PRODUCT_TYPE.SNS_PASS
      : PAYMENT_PRODUCT_TYPE.SNS_INSTALL;

    switch (targetProductType) {
      case PAYMENT_PRODUCT_TYPE.SNS_PASS:
        amount = 1;
        price = props.brand.priceSnsPass;
        priceTotal = null;
        break;

      case PAYMENT_PRODUCT_TYPE.SNS_INSTALL:
        amount = 1;
        price = props.brand.priceSnsPass;
        priceTotal = props.brand.priceSnsPass + props.brand.priceSnsInstall;
        break;
    }

    debounce(() => {
      if (props.brand.remainSnsPass) {
        window.location.reload();
      } else {
        props.showOverlays({
          content: () => {
            return (
              <Elements stripe={stripePromise}>
                <PaymentCreate
                  itemType={targetProductType}
                  amount={amount}
                  price={price}
                  priceTotal={priceTotal}
                  onSuccess={() => {
                    showPaymentCompleteModal(
                      targetProductType,
                      amount,
                      price,
                      priceTotal,
                    );
                    storeSnsPass();
                  }}
                />
              </Elements>
            );
          },
        });
      }
    }, 400);
  };

  const fetchBrand = async () => {
    await API.Brand.show({ brandToken: props.auth.token })
      .then((response) => {
        props.setBrand(response.data.brand);

        if (
          response.data.brand.hasExperiencedSnsInstall &&
          !response.data.brand.snsPassInstalledAt
        ) {
          navigate(ROUTES.SNS_PAYMENT_COMPLETE.PATH);
        }
      })
      .catch((error) => {
        props.showModal({
          text: languageTexts.celeb_detail_text_1,
        });
      });
  };

  useEffect(() => {
    fetchBrand();

    return () => {
      if (!location.pathname.startsWith('/sns')) {
        removeSessionStorage(SESSION_KEY_isPublishedFiltered);
      }
    };
  }, []);

  const renderPaymentSection = () => {
    if (props.brand?.hasExperiencedSnsInstall) {
      return null;
    }

    if (props.brand?.remainSnsPass) {
      return null;
    }

    return (
      <div className="flex flex-col items-center w-full max-w-[25rem] mx-auto my-[6.25rem]">
        <div className="flex flex-col w-full gap-[20px]">
          <div className="flex flex-row justify-center mb-[5px]">
            <span className="font-bold text-[2.25rem]">
              {languageTexts.index_text_21}
            </span>
          </div>

          <div className="flex flex-col justify-center text-center text-[15px] mb-[10px]">
            {languageTexts._index_text_22()}
          </div>

          <div className="text-sm text-[#999999] text-center">
            {languageTexts.index_text_23}
          </div>

          <div className="flex flex-col items-center">
            <Button
              className={'flex flex-row justify-center text-center py-3'}
              buttonColor={'#000000'}
              text={languageTexts.index_text_24}
              textClassName={'font-bold text-[15px]'}
              textColor={'WHITE'}
              onClick={() => {
                proceedPayment();
              }}
            />
          </div>

          <div className="flex flex-col items-center underline hover:font-bold cursor-pointer">
            <span className="text-sm text-[#999999] text-center">
              {languageTexts.index_text_25}
            </span>
          </div>

          <div className="flex flex-col items-center py-[40px] px-[20px] max-w-[400px]">
            <img
              className="w-full h-full"
              src={`/assets/images/sns/sns-intro-00.png`}
            ></img>
          </div>

          <div className="flex flex-col py-[35px] px-10 items-center gap-[30px] border border-[#E8E8E8] rounded-[10px]">
            <div className="flex flex-col items-center">
              <span className="font-bold text-[1.375rem] text-center">
                {languageTexts._index_text_26()}
              </span>
            </div>

            <div className="w-full flex flex-col gap-[10px]">
              <div className="flex flex-col items-center bg-[#F4F4F4] rounded-[10px] py-5">
                <span className="font-bold text-center text-[15px]">
                  {languageTexts.payment}
                </span>
              </div>

              <div className="flex flex-col items-center bg-[#F4F4F4] rounded-[10px] py-5">
                <span className="font-bold text-center text-[15px]">
                  {languageTexts.index_text_26}
                </span>
              </div>

              <div className="flex flex-col items-center my-5">
                <ICON_ARROW_DOWN />
              </div>

              <div className="flex flex-col items-center py-5">
                <span className="font-bold text-center text-[15px]">
                  {languageTexts.index_text_27}
                </span>
              </div>

              <div className="flex flex-col items-center my-5">
                <ICON_ARROW_DOWN />
              </div>

              <div className="flex flex-col items-center bg-[#F4F4F4] rounded-[10px] py-5">
                <span className="font-bold text-center text-[15px]">
                  {languageTexts.index_text_28}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderPostingItem = (posting: any) => {
    if (!posting) return null;

    const toggleProvide = (id: number, value: boolean) => {
      API.SnsPosting.toggle({
        brandToken: props.auth.token,
        id: id,
        value: value ? 'true' : 'false',
      })
        .then((response) => {
          props.showModal({ text: response.message });
        })
        .catch((error) => {
          props.showModal({ text: error.message });
        })
        .finally(() => {
          fetchPosting(id);
        });
    };

    return (
      <div
        key={posting.id}
        className="posting-card flex flex-col min-w-[192px] min-h-[245px]"
      >
        <div className="w-full h-full">
          <Slider {...makeSettings({})}>
            {posting.images.map((image) => {
              return (
                <img
                  onClick={() => {
                    setDetailPostingId(posting.id); // modal mode
                    // navigate(ROUTES.SNS_SHOW.SHOW(posting.id)); // navigate mode
                  }}
                  key={`postingCardImage${uniqueId()}`}
                  className="object-cover aspect-[0.7825]"
                  src={image.thumbSmUrl}
                />
              );
            })}
          </Slider>
        </div>
        <div className="flex flex-row">
          <Button
            borderColor={posting.publishedAt ? '#000000' : '#E8E8E8'}
            buttonColor={posting.publishedAt ? '#000000' : 'WHITE'}
            textColor={posting.publishedAt ? 'WHITE' : 'BLACK'}
            text={languageTexts.public}
            textClassName={'text-sm'}
            onClick={() => toggleProvide(posting.id, true)}
          />
          <Button
            buttonColor={posting.publishedAt ? 'WHITE' : '#E8E8E8'}
            borderColor={posting.publishedAt ? '#E8E8E8' : '#E8E8E8'}
            text={languageTexts.private}
            textClassName={'text-sm'}
            onClick={() => toggleProvide(posting.id, false)}
          />
        </div>
      </div>
    );
  };

  const renderSnsSection = () => {
    if (!props.brand?.hasExperiencedSnsInstall) {
      return null;
    }

    if (!props.brand?.snsPassInstalledAt) {
      return null;
    }

    return (
      <div className="flex flex-col justify-center">
        <div className="w-full max-w-[1025px] my-[4.25rem] mx-auto">
          {<SNSBrandReview />}

          <div className="flex flex-col my-[80px] gap-[1.5rem]">
            <div className="flex flex-row justify-between">
              <div>
                <span className="font-bold text-[17px]">
                  PHOTO ({postingsCount.current})
                </span>
              </div>

              <div className="flex flex-row gap-[1.875rem]">
                <span
                  className="text-[0.9375rem] underline cursor-pointer hover:font-bold"
                  onClick={() => {
                    navigate(ROUTES.SNS_POSTING_ORDER.PATH);
                  }}
                >
                  {languageTexts.index_text_9}
                </span>

                <span
                  className="text-[0.9375rem] underline cursor-pointer hover:font-bold"
                  onClick={() => {
                    navigate(ROUTES.SNS_CREATE.PATH);
                  }}
                >
                  {languageTexts.index_text_10}
                </span>
              </div>
            </div>

            <div className="flex flex-row gap-10">
              <Checkbox
                text={languageTexts.index_text_29}
                checked={isPublishedFiltered == 'true'}
                onClick={() => {
                  togglePublishedAtFilter('true');
                }}
              />
              <Checkbox
                text={languageTexts.index_text_30}
                checked={isPublishedFiltered == 'false'}
                onClick={() => {
                  togglePublishedAtFilter('false');
                }}
              />
            </div>

            <div className="flex flex-row px-[1rem] py-[2.25rem] sm:px-0">
              {postings && postings.length == 0 ? (
                <div className="py-[12rem] mx-auto">
                  <span className="font-bold text-[1.375rem] text-[#E8E8E8]">
                    {languageTexts.index_text_31}
                  </span>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-3 xl:grid-cols-5 gap-x-[1rem] gap-y-[0.875rem]">
                  {postings && postings.length
                    ? postings.map((posting) => {
                        return renderPostingItem(posting);
                      })
                    : null}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const DetailPostingModal = useCallback(
    (props) => {
      return (
        <Modal
          className="rounded-antd-modal"
          centered={true}
          open={props.isVisible}
          onOk={props.letClose}
          onCancel={props.letClose}
          footer={null}
          closable={false}
          zIndex={1010}
          width={1080}
        >
          <SNSShow
            postingId={detailPostingId}
            modalMode={true}
            letClose={props.letClose}
          />
        </Modal>
      );
    },
    [detailPostingId],
  );

  const closeDetailPostingModal = useCallback(async () => {
    setDetailPostingId(null);
    try {
      await fetchPosting(detailPostingId);
    } catch (e) {}
  }, [detailPostingId]);

  return (
    <>
      {renderPaymentSection()}
      {renderSnsSection()}
      {renderLoadingIndicator()}

      {detailPostingId === null ? null : (
        <DetailPostingModal
          isVisible={detailPostingId != null}
          letClose={closeDetailPostingModal}
        />
      )}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchProps)(SNSIndex);
